import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Hand, Loader2, Zap } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { LoginArea } from '@/components/auth/LoginArea';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useAuthor } from '@/hooks/useAuthor';
import { useToast } from '@/hooks/useToast';
import { RSVP_HASHTAG, useRsvps } from '@/hooks/useRsvps';

const VISIBLE_AVATARS = 12;

function RsvpAvatar({ pubkey }: { pubkey: string }) {
  const author = useAuthor(pubkey);
  const metadata = author.data?.metadata;
  const name = metadata?.name ?? metadata?.display_name ?? `Anon ${pubkey.slice(0, 6)}`;

  return (
    <Avatar
      title={name}
      className="size-11 border-2 border-background ring-1 ring-primary/40 transition-transform hover:scale-110"
    >
      <AvatarImage src={metadata?.picture} alt={name} loading="lazy" />
      <AvatarFallback className="bg-accent text-xs font-bold text-primary">
        {name.slice(0, 2).toUpperCase()}
      </AvatarFallback>
    </Avatar>
  );
}

export function Rsvp() {
  const { user } = useCurrentUser();
  const { mutateAsync: createEvent, isPending } = useNostrPublish();
  const rsvps = useRsvps();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [hasRsvped, setHasRsvped] = useState(false);

  const alreadyIn =
    hasRsvped || (user ? rsvps.data?.some((e) => e.pubkey === user.pubkey) : false);
  const count = (rsvsLen: number) => rsvsLen + (hasRsvped && user && !rsvps.data?.some((e) => e.pubkey === user.pubkey) ? 1 : 0);

  const handleRsvp = async () => {
    try {
      await createEvent({
        kind: 1,
        content:
          'Count me in for SheSats Nairobi — a Bitcoin & Nostr hackathon for women in Nairobi, 14–15 Nov 2026. See you there! #SheSatsNairobi',
        tags: [['t', RSVP_HASHTAG]],
      });
      setHasRsvped(true);
      toast({
        title: 'You’re in! ⚡',
        description: 'Your hand is raised on Nostr. We can’t wait to build with you.',
      });
      queryClient.invalidateQueries({ queryKey: ['shesats-rsvps'] });
    } catch {
      toast({
        variant: 'destructive',
        title: 'Couldn’t publish',
        description: 'Please check your relay connection and try again.',
      });
    }
  };

  return (
    <section id="rsvp" className="relative isolate scroll-mt-20 overflow-hidden bg-accent py-20 text-accent-foreground sm:py-28">
      <div className="absolute left-1/2 top-0 -z-10 h-72 w-[48rem] -translate-x-1/2 rounded-full bg-primary/20 blur-3xl" aria-hidden />

      <div className="container flex flex-col items-center gap-10 text-center">
        <div className="max-w-2xl space-y-4">
          <p className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-[0.3em] text-primary">
            <Hand className="size-4" aria-hidden />
            Raise Your Hand
          </p>
          <h2 className="font-display text-4xl font-semibold tracking-tight text-balance text-white sm:text-5xl">
            Claim your spot — the Nostr way
          </h2>
          <p className="text-lg leading-relaxed text-accent-foreground/70">
            No forms, no email lists. Log in with Nostr and post with{' '}
            <span className="font-semibold text-primary">#SheSatsNairobi</span> to say
            you’re coming. Your note is your ticket — public, portable, yours.
          </p>
        </div>

        <div className="flex flex-col items-center gap-6">
          {user ? (
            alreadyIn ? (
              <p className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-6 py-3 font-semibold text-primary">
                <Zap className="size-5" fill="currentColor" aria-hidden />
                You’re on the list — see you in Nairobi!
              </p>
            ) : (
              <Button
                size="lg"
                onClick={handleRsvp}
                disabled={isPending}
                className="h-12 rounded-full px-8 text-base font-semibold shadow-lg shadow-primary/30"
              >
                {isPending ? (
                  <Loader2 className="size-5 animate-spin" aria-hidden />
                ) : (
                  <Zap className="size-5" fill="currentColor" aria-hidden />
                )}
                {isPending ? 'Posting…' : 'Count me in'}
              </Button>
            )
          ) : (
            <div className="flex flex-col items-center gap-3">
              <LoginArea className="max-w-60" />
              <p className="text-sm text-accent-foreground/60">
                New to Nostr? “Join” will set you up with a key in seconds.
              </p>
            </div>
          )}

          {/* Avatar wall */}
          {rsvps.isLoading ? (
            <div className="flex items-center gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="size-11 rounded-full bg-white/10" />
              ))}
            </div>
          ) : rsvps.data && rsvps.data.length > 0 ? (
            <div className="flex flex-col items-center gap-3">
              <div className="flex flex-wrap items-center justify-center gap-2">
                {rsvps.data.slice(0, VISIBLE_AVATARS).map((event) => (
                  <RsvpAvatar key={event.pubkey} pubkey={event.pubkey} />
                ))}
                {rsvps.data.length > VISIBLE_AVATARS && (
                  <span className="grid size-11 place-items-center rounded-full bg-primary text-xs font-bold text-primary-foreground">
                    +{rsvps.data.length - VISIBLE_AVATARS}
                  </span>
                )}
              </div>
              <p className="text-sm font-medium text-accent-foreground/70">
                {count(rsvps.data.length)}{' '}
                {count(rsvps.data.length) === 1 ? 'builder has' : 'builders have'} raised
                their hand
              </p>
            </div>
          ) : (
            <p className="rounded-full border border-dashed border-white/25 px-6 py-3 text-sm text-accent-foreground/60">
              Be the first to raise your hand ⚡
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
